import { RequireAuthAction } from "@/components/auth/RequireAuthAction";
import { TokenLabel } from "@/components/brand/TokenAmount";
import { Button } from "@/components/ui/Button";
import {
	useStakeMmk,
	useStakingBalance,
	useUnstakeMmk,
} from "@/hooks/useGovernance";
import { useAuth } from "@/lib/auth";
import { BrandTokenMark } from "@/components/brand/BrandTokenMark";
import { formatAmountPlain, formatNum, parseAmount } from "@/lib/utils";
import { useState } from "react";

export function MmkStakeWidget() {
	const { balances, isAuthenticated } = useAuth();
	const { data: staked = 0, isLoading } = useStakingBalance();
	const stake = useStakeMmk();
	const unstake = useUnstakeMmk();
	const [amount, setAmount] = useState("");
	const n = parseAmount(amount);

	return (
		<div className="card-surface space-y-4 p-5">
			<div className="flex items-start gap-3">
				<BrandTokenMark token="MMK" size="lg" />
				<div>
				<p className="section-eyebrow">Vote weight</p>
				<h2 className="text-lg font-bold">Stake MMK</h2>
				<p className="mt-1 text-xs text-muted-foreground">
					Only <strong className="text-foreground">staked</strong> MMK grants
					governance weight. Unstaked wallet MMK does not vote.
				</p>
				</div>
			</div>

			<div className="grid gap-3 sm:grid-cols-2">
				<div className="rounded-xl border border-border bg-muted/30 p-3">
					<p className="text-[10px] uppercase text-muted-foreground">Staked</p>
					<p className="font-mono-amount text-xl font-bold">
						{isLoading ? "…" : formatNum(staked, 2)} MMK
					</p>
				</div>
				<div className="rounded-xl border border-border bg-muted/30 p-3">
					<p className="text-[10px] uppercase text-muted-foreground">
						Wallet MMK
					</p>
					<p className="font-mono-amount text-xl font-bold">
						{formatNum(balances.MMK, 2)} MMK
					</p>
				</div>
			</div>

			<label className="block">
				<span className="text-xs font-semibold text-muted-foreground">
					Amount
				</span>
				<div className="mt-1 flex items-center gap-2">
					<input
						value={amount}
						onChange={(e) => setAmount(e.target.value)}
						inputMode="decimal"
						className="h-11 flex-1 rounded-xl border border-border bg-muted/40 px-3 font-mono-amount outline-none focus:border-primary"
					/>
					<TokenLabel id="MMK" size="sm" />
				</div>
			</label>

			<div className="flex flex-wrap gap-2">
				<RequireAuthAction
					labelWhenUnauthed="Connect to stake"
					disabled={stake.isPending || n <= 0 || n > balances.MMK + 1e-9}
					onAuthenticated={() => stake.mutate(n)}
					className="flex-1"
				>
					{stake.isPending ? "Staking…" : "Stake"}
				</RequireAuthAction>
				<Button
					variant="secondary"
					className="flex-1"
					disabled={
						!isAuthenticated ||
						unstake.isPending ||
						n <= 0 ||
						n > staked + 1e-9
					}
					onClick={() => unstake.mutate(n)}
				>
					{unstake.isPending ? "Unstaking…" : "Unstake"}
				</Button>
				<Button
					variant="outline"
					size="sm"
					type="button"
					onClick={() => setAmount(formatAmountPlain(balances.MMK, 4))}
				>
					Max wallet
				</Button>
				<Button
					variant="outline"
					size="sm"
					type="button"
					onClick={() => setAmount(formatAmountPlain(staked, 4))}
				>
					Max staked
				</Button>
			</div>
		</div>
	);
}
